import { useState, useCallback, useEffect } from 'react'
import { Outlet, Navigate, useLocation, useMatch } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { BusinessProvider, useBusiness } from '@/contexts/BusinessContext'
import { useAuth } from '@/contexts/AuthContext'
import { LayoutProvider, useLayout } from '@/contexts/LayoutContext'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'

/**
 * Keep BusinessContext.currentBusiness in sync with the URL.
 *
 * - `/inbox/:businessId/*` (scoped inbox or a conversation) → match that business.
 * - `/inbox` exactly (aggregate inbox) → null, so TopBar shows "All Businesses".
 * - Every other route (settings, contacts, contacts/:id, templates, admin,
 *   search/:businessId) → preserve whatever the user last picked. Forcing null
 *   here made CRM pages prompt "Select a business" even when the user had just
 *   come from a scoped inbox.
 *
 * Lives inside BusinessProvider so it can call useBusiness.
 */
function BusinessRouteSync() {
  const scopedInboxMatch = useMatch('/inbox/:businessId/*')
  const aggregateInboxMatch = useMatch('/inbox')
  const routeBusinessId = scopedInboxMatch?.params.businessId
  const { currentBusiness, getBusinessById, setCurrentBusiness } = useBusiness()
  const { membershipsLoading } = useAuth()

  useEffect(() => {
    if (membershipsLoading) return
    if (routeBusinessId) {
      const target = getBusinessById(routeBusinessId)
      if (target && currentBusiness?.id !== target.id) {
        setCurrentBusiness(target)
      }
    } else if (aggregateInboxMatch && currentBusiness !== null) {
      // Only the aggregate /inbox root is truly "All Businesses" — force null there.
      setCurrentBusiness(null)
    }
  }, [
    routeBusinessId,
    aggregateInboxMatch,
    membershipsLoading,
    currentBusiness,
    getBusinessById,
    setCurrentBusiness,
  ])

  return null
}

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 200, damping: 25 },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: 0.15 },
  },
}

/** Redirect users with zero memberships to /settings (onboarding). */
function useOnboardingRedirect() {
  const { memberships, membershipsLoading } = useAuth()
  const location = useLocation()

  return (
    !membershipsLoading &&
    memberships.length === 0 &&
    location.pathname !== '/settings'
  )
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const shouldRedirectToOnboarding = useOnboardingRedirect()

  const handleMobileClose = useCallback(() => setMobileOpen(false), [])
  const handleMobileToggle = useCallback(() => setMobileOpen((prev) => !prev), [])

  // Reset scroll on route change. React Router doesn't restore scroll for
  // SPA navigations, and AnimatePresence's keyed re-mount preserved the
  // outgoing page's scroll position — landing on /pipeline already half-
  // scrolled with the H1 above the viewport. Skip when navigating *to* a
  // location with a hash anchor; the deep-link handler should win.
  useEffect(() => {
    if (location.hash) return
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname, location.hash])

  // Close the slide-in drawer on any route change. NavLink onClicks handle
  // sidebar-internal navigation, but the business switcher / inline links /
  // burger-on-deck flows can leave the drawer hanging otherwise.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  if (shouldRedirectToOnboarding) {
    return <Navigate to="/settings" replace />
  }

  return (
    <BusinessProvider>
      <LayoutProvider>
        <BusinessRouteSync />
        <div className="min-h-[100dvh] bg-bg">
          {/* Skip navigation link for keyboard users */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-accent focus:text-white focus:rounded-lg focus:text-sm focus:font-medium focus:shadow-lg"
          >
            Skip to main content
          </a>
          <Sidebar mobileOpen={mobileOpen} onMobileClose={handleMobileClose} />
          <TopBar onMobileMenuToggle={handleMobileToggle} />
          <AppShellMain locationKey={location.pathname} />
        </div>
      </LayoutProvider>
    </BusinessProvider>
  )
}

/**
 * Main pane. Split out so it can consume LayoutContext (the provider above
 * is its parent), letting a page opt out of the max-w wrapper and / or the
 * 252px sidebar offset.
 */
function AppShellMain({ locationKey }: { locationKey: string }) {
  const { fullWidth, sidebarHidden } = useLayout()
  const offset = sidebarHidden ? '' : 'md:ml-[252px]'
  const padding = fullWidth ? '' : 'px-4 md:px-8'
  return (
    <main id="main-content" className={`${offset} mt-[88px] ${padding} pb-8 pt-2`}>
      <div className={fullWidth ? '' : 'max-w-[1400px] mx-auto'}>
        <ErrorBoundary>
          <AnimatePresence mode="wait">
            <motion.div
              key={locationKey}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </ErrorBoundary>
      </div>
    </main>
  )
}
